"use client"

import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from "react-router-dom";

// --- Helper Components (Single File Structure) ---

const FilterModal = ({ title, options, selected, onSave, onClose }) => {
    const [currentSelection, setCurrentSelection] = useState(selected);

    const handleSimpleCheckboxChange = (value) => {
        setCurrentSelection(prev => {
            const newSelection = [...prev];
            if (newSelection.includes(value)) {
                return newSelection.filter(item => item !== value);
            } else {
                return [...newSelection, value];
            }
        });
    };

    const handleSimpleGroupChange = (groupValues) => {
         setCurrentSelection(prev => {
            const areAllSelected = groupValues.every(v => prev.includes(v));
            if (areAllSelected) {
                return prev.filter(item => !groupValues.includes(item));
            } else {
                return [...new Set([...prev, ...groupValues])];
            }
        });
    };

    const totalSelectedCount = currentSelection.length;

    const renderOptions = () => {
        return Object.entries(options).map(([category, values]) => (
            <div key={category} className="mb-4">
                <h4 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-2 flex items-center">
                     <input
                        type="checkbox"
                        id={`group-${category}`}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={values.every(v => currentSelection.includes(v))}
                        onChange={() => handleSimpleGroupChange(values)}
                    />
                    <label htmlFor={`group-${category}`} className="ml-2">{category}</label>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {values.map(value => (
                        <div key={value} className="flex items-center">
                            <input
                                type="checkbox"
                                id={`val-${value}`}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                checked={currentSelection.includes(value)}
                                onChange={() => handleSimpleCheckboxChange(value)}
                            />
                            <label htmlFor={`val-${value}`} className="ml-2 text-sm">{value}</label>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-full max-h-[92vh] flex flex-col">
                <header className="p-4 border-b flex justify-between items-center flex-shrink-0">
                    <h3 className="text-xl font-bold text-gray-800">{title}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">&times;</button>
                </header>
                <main className="p-4 sm:p-6 overflow-y-auto flex-grow">
                    {renderOptions()}
                </main>
                <footer className="p-4 border-t flex flex-col gap-3 bg-gray-50 flex-shrink-0 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-indigo-600 font-semibold">{totalSelectedCount.toLocaleString()}件 選択中</span>
                    <div className="grid grid-cols-2 gap-2 sm:flex">
                        <button onClick={onClose} className="py-2 px-4 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold">キャンセル</button>
                        <button onClick={() => onSave(currentSelection)} className="py-2 px-4 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 font-semibold">選択を反映</button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

const RadioFilter = ({ label, name, value, onChange }) => (
    <div>
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <div className="flex items-center space-x-4 mt-1">
            <label><input type="radio" name={name} value="any" checked={value === 'any'} onChange={onChange} className="mr-1"/>問わない</label>
            <label><input type="radio" name={name} value="yes" checked={value === 'yes'} onChange={onChange} className="mr-1"/>有り</label>
        </div>
    </div>
);

const UsageLimitCard = ({ limits, plan }) => {
    if (!limits) return null;
    const planName = plan === 'free' ? '無料プラン' : plan === 'trial' ? 'トライアル' : 'プラン';
    
    const renderProgressBar = (usage) => {
        const current = usage.current || 0;
        const limit = usage.limit; // null の可能性がある
        
        // ★修正: 上限なし(null)の場合の表示
        if (limit === null) {
            return (
                <div>
                    <div className="flex justify-between text-sm mb-1">
                        <span className="font-bold text-indigo-600">今月はプラン範囲内でダウンロード可能です</span>
                    </div>
                    <div className="w-full bg-indigo-100 rounded-full h-2.5">
                        <div className="bg-indigo-600 h-2.5 rounded-full w-full opacity-50"></div>
                    </div>
                </div>
            );
        }

        const percentage = limit > 0 ? (current / limit) * 100 : 0;
        const isExceeded = percentage > 100;
        const barColor = isExceeded ? 'bg-red-500' : 'bg-blue-600';
        
        return (
            <div>
                <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{current.toLocaleString()} / {limit.toLocaleString()} 件</span>
                    <span className={isExceeded ? 'text-red-600 font-bold' : 'text-gray-500'}>{percentage.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className={`${barColor} h-2.5 rounded-full`} style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-400 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 {planName} ご利用状況</h3>
            <div className="space-y-4">
                <div><p className="text-sm font-semibold text-gray-600 mb-2">今月のダウンロード数</p>{renderProgressBar(limits.downloads)}</div>
            </div>
        </div>
    );
};


// --- メインページコンポーネント ---
export default function ListDownloadPage() {
    const { user } = useOutletContext();
    const [companies, setCompanies] = useState([]);
    const [pagination, setPagination] = useState({ totalItems: 0, currentPage: 1, totalPages: 1 });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const initialFilters = {
        searchTerm: '', corporateNumber: '', industry_class: [],
        capitalMin: '', capitalMax: '', employeesMin: '', employeesMax: '',
        establishmentYearStart: '', establishmentYearEnd: '', business_tags: [],
        hasUrl: 'any', hasContactUrl: 'any', hasPhoneNumber: 'any'
    };
    const [filters, setFilters] = useState(initialFilters);

    const [filterOptions, setFilterOptions] = useState({ industry: {}, business_tags: {} });
    const [isModalOpen, setIsModalOpen] = useState({ industry_class: false, business_tags: false });
    const [selectedCompanies, setSelectedCompanies] = useState(new Set());
    
    const [usageStatus, setUsageStatus] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    // 🚀 修正: 固定ドメインを排除し、現在のオリジンを自動取得
    const API_URL = window.location.hostname === 'localhost' 
      ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
      : window.location.origin;

    // 🚀 修正: OEM社長(admin)がプルダウンで選択したクライアントを反映できるように変更
    const clientId = useMemo(() => (
      user?.role === "admin" || user?.role === "admin_staff" 
        ? localStorage.getItem("selectedClientId") || "" 
        : user?.client_id
    ), [user]);

    useEffect(() => {
        if (!clientId) {
            setInitialLoading(false);
            return;
        }

        const fetchInitialData = async () => {
            setInitialLoading(true);
            setError(null);
            try {
                await Promise.all([
                    (async () => {
                        const res = await fetch(`/api/usage-status?clientId=${clientId}`, {
                            headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
                        });
                        const data = await res.json();
                        if (data.success) setUsageStatus(data);
                        else throw new Error("利用状況の取得に失敗しました。");
                    })(),
                    (async () => {
                        const filterRes = await fetch('/api/search-filters', {
                            headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
                        });
                        const filterData = await filterRes.json();
                        if (filterData.success) {
                            setFilterOptions({
                                industry: filterData.filters['業種'] || {},
                                business_tags: filterData.filters['事業タグ'] || {},
                            });
                        } else {
                            throw new Error("フィルター選択肢の読み込みに失敗しました。");
                        }
                    })(),
                    (async () => { // ★★★ 提案: 認証ヘッダーを追加 ★★★
                        const res = await fetch(`/api/list-download/companies`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...getAuthHeader() }, // ★ 2. 認証ヘッダーを追加
                            body: JSON.stringify({ page: 1, limit: 100, client_id: clientId, filters: initialFilters })
                        });
                        const data = await res.json();
                        if (data.success) {
                            setCompanies(data.data);
                            setPagination(data.pagination);
                        } else {
                            throw new Error(data.error || "企業リストの初期読み込みに失敗しました。");
                        }
                    })()
                ]);
            } catch (err) {
                setError(err.message);
            } finally {
                setInitialLoading(false);
            }
        };

        fetchInitialData();
    }, [clientId]);

    const fetchCompanies = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const body = {
                page: page,
                limit: 100,
                client_id: clientId,
                filters: filters
            };

            const res = await fetch(`/api/list-download/companies`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() }, // ★ 2. 認証ヘッダーを追加
                body: JSON.stringify(body)
            });

            if (!res.ok) throw new Error(`サーバーエラー: ${res.statusText}`);
            
            const data = await res.json();
            if (data.success) {
                setCompanies(data.data);
                setPagination(data.pagination);
            } else {
                throw new Error(data.error || "企業の検索に失敗しました。");
            }
        } catch (err) {
            setError(err.message);
            setCompanies([]);
        } finally {
            setLoading(false);
        }
    }, [filters, clientId]);
    
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleModalSave = (filterName, selection) => {
        setFilters(prev => ({ ...prev, [filterName]: selection }));
        setIsModalOpen(prev => ({ ...prev, [filterName]: false }));
    };
    
    const handleSearch = () => {
        setPagination(p => ({...p, currentPage: 1}));
        fetchCompanies(1);
        setSelectedCompanies(new Set());
    };
    
    const handleClearFilters = () => {
        setFilters(initialFilters);
        setTimeout(() => { 
            setPagination(p => ({...p, currentPage: 1}));
            fetchCompanies(1);
            setSelectedCompanies(new Set());
        }, 0);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && pagination && newPage <= pagination.totalPages) {
            fetchCompanies(newPage);
        }
    };

    const remainingDownloads = useMemo(() => {
        if (!usageStatus || !usageStatus.limits) return 0;
        const limit = usageStatus.limits.downloads.limit;
        const current = usageStatus.limits.downloads.current || 0;
        
        // limit が null の場合は上限なし扱いなので、非常に大きな数値を返す
        if (limit === null) return 10000000; 
        
        return Math.max(0, limit - current);
    }, [usageStatus]);

    const handleSelectCompany = (id) => {
        setSelectedCompanies(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                if (usageStatus?.limits && newSet.size >= remainingDownloads) {
                    alert(`ダウンロードの上限に達しているため、これ以上選択できません。\n現在の残りダウンロード可能件数: ${remainingDownloads}件`);
                } else {
                    newSet.add(id);
                }
            }
            return newSet;
        });
    };
    
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const pageCompanyIds = companies.map(c => c.id);
            if(usageStatus?.limits && pageCompanyIds.length > remainingDownloads) {
                alert(`このページの全件(${pageCompanyIds.length}件)を選択すると、ダウンロード上限(${remainingDownloads}件)を超えてしまいます。`);
                return;
            }
            setSelectedCompanies(new Set(pageCompanyIds));
        } else {
            setSelectedCompanies(new Set());
        }
    };

    const isAllSelectedOnPage = useMemo(() => {
        return companies.length > 0 && companies.every(c => selectedCompanies.has(c.id));
    }, [selectedCompanies, companies]);
    
    const countSelected = (filterName) => {
       return filters[filterName]?.length || 0;
    }
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            const year = date.getUTCFullYear();
            const month = date.getUTCMonth() + 1;
            const day = date.getUTCDate();
            return `${year}/${month}/${day}`;
        } catch (e) {
            return dateString;
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        setError(null);
        try {
            const isSelectionDownload = selectedCompanies.size > 0;
            // nullチェック追加
            const totalItems = pagination.totalItems || 0;
            const downloadCount = isSelectionDownload ? selectedCompanies.size : totalItems;

            if (downloadCount === 0) {
                setError("ダウンロード対象の企業が0件です。");
                setIsDownloading(false);
                return;
            }

            if (usageStatus?.limits && downloadCount > remainingDownloads) {
                setError(`ダウンロード上限を超えています。残りダウンロード可能件数: ${remainingDownloads}件`);
                setIsDownloading(false);
                return;
            }

            const LARGE_DOWNLOAD_THRESHOLD = 50000;
            if (downloadCount > LARGE_DOWNLOAD_THRESHOLD) {
                const message = `ダウンロード対象が ${downloadCount.toLocaleString()}件と非常に多いです。\n処理に数分かかる場合がありますが、続行しますか？`;
                if (!window.confirm(message)) {
                    setIsDownloading(false);
                    return; 
                }
            }

            const body = {
                clientId: clientId,
                targetIds: isSelectionDownload ? Array.from(selectedCompanies) : null,
                filters: isSelectionDownload ? null : filters,
            };

            const response = await fetch('/api/list-download/csv', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() }, // ★ 2. 認証ヘッダーを追加
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || `ダウンロードに失敗しました (HTTP ${response.status})`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'company_list.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            
            const res = await fetch(`/api/usage-status?clientId=${clientId}`, {
                headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
            });
            const data = await res.json();
            if (data.success) setUsageStatus(data);
            setSelectedCompanies(new Set());

        } catch (err) {
            setError(err.message);
        } finally {
            setIsDownloading(false);
        }
    };
    
    if (initialLoading) {
        return (
            <div className="flex items-center justify-center h-[80vh]">
                <div className="flex flex-col items-center">
                    <svg className="animate-spin h-10 w-10 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-lg font-semibold text-gray-700">ページを準備しています...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-100 min-h-screen p-3 sm:p-6 lg:p-8 font-sans">
            {isModalOpen.industry_class && <FilterModal title="業種で絞り込み" options={filterOptions.industry} selected={filters.industry_class} onSave={(selection) => handleModalSave('industry_class', selection)} onClose={() => setIsModalOpen(p => ({...p, industry_class: false}))} />}
            {isModalOpen.business_tags && <FilterModal title="事業タグで絞り込み" options={filterOptions.business_tags} selected={filters.business_tags} onSave={(selection) => handleModalSave('business_tags', selection)} onClose={() => setIsModalOpen(p => ({...p, business_tags: false}))} />}

            <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
                <header className="p-4 sm:p-6 border-b">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800">リストダウンロード</h1>
                    <p className="text-gray-600 mt-1">条件を指定して、高品質な企業リストを検索・ダウンロードします。</p>
                </header>
                
                <div className="p-4 sm:p-6">
                    {usageStatus && usageStatus.limits && (
                        <UsageLimitCard limits={usageStatus.limits} plan={usageStatus.plan} />
                    )}
                </div>

                <section className="p-4 sm:p-6 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        <div className="md:col-span-2 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <input type="text" name="searchTerm" placeholder="フリーワード (会社名、住所、事業内容など)" value={filters.searchTerm} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                            <input type="text" name="corporateNumber" placeholder="法人番号 (ハイフンなし13桁)" value={filters.corporateNumber} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                        </div>
                        
                        <button onClick={() => setIsModalOpen(p => ({...p, industry_class: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative">業種... <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('industry_class')}</span></button>
                        <button onClick={() => setIsModalOpen(p => ({...p, business_tags: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative">事業タグ... <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('business_tags')}</span></button>

                        <div>
                            <label className="text-sm font-medium text-gray-700">資本金 (万円)</label>
                            <div className="flex items-center gap-2">
                                <input type="number" name="capitalMin" placeholder="下限" value={filters.capitalMin} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                                <span className="text-gray-500">-</span>
                                <input type="number" name="capitalMax" placeholder="上限" value={filters.capitalMax} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                            </div>
                        </div>
                         <div>
                            <label className="text-sm font-medium text-gray-700">従業員数 (人)</label>
                            <div className="flex items-center gap-2">
                                <input type="number" name="employeesMin" placeholder="下限" value={filters.employeesMin} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                                <span className="text-gray-500">-</span>
                                <input type="number" name="employeesMax" placeholder="上限" value={filters.employeesMax} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700">設立年</label>
                            <div className="flex items-center gap-2">
                                <input type="number" name="establishmentYearStart" placeholder="YYYY" value={filters.establishmentYearStart} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                                <span className="text-gray-500">-</span>
                                <input type="number" name="establishmentYearEnd" placeholder="YYYY" value={filters.establishmentYearEnd} onChange={handleFilterChange} className="p-2 border rounded-md w-full"/>
                            </div>
                        </div>

                         <RadioFilter label="公式サイトURL" name="hasUrl" value={filters.hasUrl} onChange={handleFilterChange} />
                         <RadioFilter label="問合せURL" name="hasContactUrl" value={filters.hasContactUrl} onChange={handleFilterChange} />
                         <RadioFilter label="電話番号" name="hasPhoneNumber" value={filters.hasPhoneNumber} onChange={handleFilterChange} />
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-3 sm:flex sm:justify-end">
                        <button onClick={handleClearFilters} className="py-2 px-6 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold">クリア</button>
                        <button id="search-button" onClick={handleSearch} className="py-2 px-8 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-semibold shadow-sm" disabled={loading}>
                            {loading ? '検索中...' : 'この条件で検索'}
                        </button>
                    </div>
                </section>

                <section className="p-4 sm:p-6 border-t">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                        <div>
                            {/* [修正] totalItems が null の場合のフォールバックを追加 */}
                            <h2 className="text-xl font-semibold text-gray-800">検索結果: <span className="text-indigo-600">{(pagination.totalItems || 0).toLocaleString()}</span>件</h2>
                            {usageStatus && usageStatus.limits && (
                                <p className="text-sm text-yellow-700 font-semibold mt-1">
                                    今月の残りダウンロード可能件数: {remainingDownloads.toLocaleString()}件
                                </p>
                            )}
                        </div>
                        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                            <span className="text-gray-600 font-medium">{selectedCompanies.size.toLocaleString()}件 選択中</span>
                            <button onClick={handleDownload} className="w-full py-2 px-4 rounded-lg bg-green-600 text-white hover:bg-green-700 font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto" disabled={isDownloading}>
                                {isDownloading ? '処理中...' : selectedCompanies.size > 0 ? `選択した${selectedCompanies.size.toLocaleString()}件をCSVダウンロード` : '検索結果をCSVダウンロード'}
                            </button>
                        </div>
                    </div>

                    {error && <div className="text-red-500 bg-red-100 p-3 rounded-md mb-4">{error}</div>}
                    
                    <div className="overflow-hidden rounded-lg border border-gray-100 md:border-0">
                        <div className="divide-y divide-gray-100 md:hidden">
                            {loading ? (
                                <div className="p-8 text-center text-gray-500">読み込み中...</div>
                            ) : companies.length > 0 ? (
                                companies.map(company => {
                                    const isChecked = selectedCompanies.has(company.id);
                                    const canBeChecked = isChecked || !usageStatus?.limits || selectedCompanies.size < remainingDownloads;
                                    return (
                                        <div key={`${company.id}-mobile`} className="bg-white p-4">
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => handleSelectCompany(company.id)}
                                                    disabled={!canBeChecked}
                                                    className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <p className="break-words font-semibold text-gray-900">{company.company || '-'}</p>
                                                    <p className="mt-1 text-xs text-gray-500">法人番号: {company.corporate_number || '-'}</p>
                                                </div>
                                            </div>
                                            <div className="mt-3 space-y-1 text-xs text-gray-600">
                                                <p><span className="font-semibold text-gray-500">業種:</span> {company.industry_class || '-'}</p>
                                                <p><span className="font-semibold text-gray-500">住所:</span> {company.address || '-'}</p>
                                                <p><span className="font-semibold text-gray-500">電話:</span> {company.phone || '-'}</p>
                                                <p><span className="font-semibold text-gray-500">資本金:</span> {company.capital?.toLocaleString() ?? '-'}万円</p>
                                                <p><span className="font-semibold text-gray-500">従業員:</span> {company.employee_count?.toLocaleString() ?? '-'}人</p>
                                                <p><span className="font-semibold text-gray-500">設立日:</span> {formatDate(company.establishment_date)}</p>
                                                <p><span className="font-semibold text-gray-500">代表者:</span> {company.representative_name || '-'}</p>
                                                <p className="break-words"><span className="font-semibold text-gray-500">事業タグ:</span> {company.business_tags || '-'}</p>
                                                <p className="break-all">
                                                    <span className="font-semibold text-gray-500">会社URL:</span>{' '}
                                                    {company.url ? <a href={company.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">{company.url}</a> : '-'}
                                                </p>
                                                <p className="break-all">
                                                    <span className="font-semibold text-gray-500">問合せURL:</span>{' '}
                                                    {company.contact_url ? <a href={company.contact_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">{company.contact_url}</a> : '-'}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="p-8 text-center text-gray-500">該当する企業が見つかりませんでした。</div>
                            )}
                        </div>
                        <div className="hidden overflow-x-auto md:block">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th scope="col" className="p-4"><input type="checkbox" onChange={handleSelectAll} checked={isAllSelectedOnPage} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"/></th>
                                    <th scope="col" className="px-6 py-3">法人番号</th>
                                    <th scope="col" className="px-6 py-3">会社名</th>
                                    <th scope="col" className="px-6 py-3">会社URL</th>
                                    <th scope="col" className="px-6 py-3">問合せURL</th>
                                    <th scope="col" className="px-6 py-3">業種</th>
                                    {/* [修正] 住所の列を再追加 */}
                                    <th scope="col" className="px-6 py-3">住所</th>
                                    <th scope="col" className="px-6 py-3">電話番号</th>
                                    <th scope="col" className="px-6 py-3">資本金(万円)</th>
                                    <th scope="col" className="px-6 py-3">従業員数</th>
                                    <th scope="col" className="px-6 py-3">設立日</th>
                                    <th scope="col" className="px-6 py-3">代表者名</th>
                                    <th scope="col" className="px-6 py-3">事業タグ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    /* [修正] colSpanを13に修正 */
                                    <tr><td colSpan="13" className="text-center p-8 text-gray-500">読み込み中...</td></tr>
                                ) : companies.length > 0 ? (
                                    companies.map(company => {
                                        const isChecked = selectedCompanies.has(company.id);
                                        const canBeChecked = isChecked || !usageStatus?.limits || selectedCompanies.size < remainingDownloads;
                                        return (
                                            <tr key={company.id} className="bg-white border-b hover:bg-gray-50">
                                                <td className="w-4 p-4">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isChecked} 
                                                        onChange={() => handleSelectCompany(company.id)} 
                                                        disabled={!canBeChecked}
                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">{company.corporate_number}</td>
                                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{company.company}</th>
                                                <td className="px-6 py-4"><a href={company.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{company.url}</a></td>
                                                <td className="px-6 py-4"><a href={company.contact_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{company.contact_url}</a></td>
                                                <td className="px-6 py-4">{company.industry_class}</td>
                                                {/* [修正] 住所の列を再追加 */}
                                                <td className="px-6 py-4">{company.address}</td>
                                                <td className="px-6 py-4">{company.phone}</td>
                                                {/* [修正] null/undefined の場合の安全な表示 */}
                                                <td className="px-6 py-4 text-right">{company.capital?.toLocaleString() ?? '-'}</td>
                                                <td className="px-6 py-4 text-right">{company.employee_count?.toLocaleString() ?? '-'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{formatDate(company.establishment_date)}</td>
                                                <td className="px-6 py-4">{company.representative_name}</td>
                                                <td className="px-6 py-4">{company.business_tags}</td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                     /* [修正] colSpanを13に修正 */
                                     <tr><td colSpan="13" className="text-center p-8 text-gray-500">該当する企業が見つかりませんでした。</td></tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                    </div>

                    {/* [修正] totalItems の安全な参照 */}
                    {(pagination.totalItems || 0) > 0 && (
                        <nav className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
                            <span className="text-sm font-normal text-gray-500 mb-2 sm:mb-0">
                                全<span className="font-semibold text-gray-900"> {(pagination.totalItems || 0).toLocaleString()} </span>件中 
                                <span className="font-semibold text-gray-900"> {Math.min((pagination.currentPage - 1) * 100 + 1, pagination.totalItems || 0).toLocaleString()}-{Math.min(pagination.currentPage * 100, pagination.totalItems || 0).toLocaleString()} </span>
                                表示中
                            </span>
                            <ul className="inline-flex -space-x-px text-sm h-8">
                                <li><button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage <= 1 || loading} className="flex items-center justify-center px-3 h-8 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">前へ</button></li>
                                <li><span className="flex items-center justify-center px-4 h-8 leading-tight text-gray-500 bg-white border-y border-gray-300">Page {pagination.currentPage} of {pagination.totalPages}</span></li>
                                <li><button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage >= pagination.totalPages || loading} className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">次へ</button></li>
                            </ul>
                        </nav>
                    )}
                </section>
            </div>
        </div>
    );
}
